/**
 * Customer-view preview of a quote — what the homeowner will see at
 * `/q/{token}` once the quote is sent. Authed: any org user (or
 * pencilout_admin) who can view the quote can preview it.
 *
 * Critical difference vs `/q/{token}`: this route fetches from
 * `GET /quotes/{id}/customer-preview` (authed, no side effects). It
 * does NOT increment ``view_count``, does NOT set
 * ``client_viewed_at``, and does NOT fire the "client opened your
 * quote" push notification — so a contractor checking the rendering
 * doesn't pollute the genuine view-tracking signal.
 *
 * Reuses the customer-facing visual blocks (`Letterhead`,
 * `BillingBlock`, `LineItems`, `Totals`) exported from
 * ``PublicQuoteView`` — keeps preview pixel-identical to what
 * the homeowner sees without duplicating layout. The signature
 * block + comment thread are intentionally absent: this is a
 * read-only mock-up, not the live customer surface.
 *
 * Route: ``/quotes/:quoteId/preview``
 */
import { ArrowLeft, Eye } from "lucide-react";
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useQuoteCustomerPreview } from "@/api/hooks";
import {
  BillingBlock,
  ChangeOrderList,
  Letterhead,
  LineItems,
  Totals,
} from "@/routes/PublicQuoteView";
import { formatTime } from "@/lib/format";

export function QuoteCustomerPreviewPage() {
  const { quoteId = "" } = useParams<{ quoteId: string }>();
  const previewQuery = useQuoteCustomerPreview(quoteId || undefined);

  // Mark the tab title so the contractor can spot the preview tab
  // among many open tabs while comparing what their homeowner will
  // see. Reset on unmount.
  useEffect(() => {
    document.title = "PencilOut · Customer preview";
    return () => {
      document.title = "PencilOut";
    };
  }, []);

  if (previewQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper text-sm text-graphite-soft">
        Loading preview…
      </div>
    );
  }

  if (previewQuery.error || !previewQuery.data) {
    const detail =
      previewQuery.error instanceof ApiError
        ? previewQuery.error.detail
        : "We couldn't load this quote preview.";
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-6 text-graphite">
        <div className="w-full max-w-md text-center">
          <div className="mb-6">
            <span className="brand-wordmark text-3xl">PencilOut</span>
          </div>
          <div className="rounded-lg border border-rule bg-paper-warm p-6">
            <h1 className="font-mono text-lg font-bold tracking-tight">
              Couldn't load preview
            </h1>
            <p className="mt-2 text-sm text-graphite-soft">{detail}</p>
          </div>
          <Link
            to={`/quotes/${quoteId}`}
            className="mt-4 inline-flex items-center gap-1 text-sm text-graphite-soft hover:text-graphite"
          >
            <ArrowLeft className="h-3 w-3" aria-hidden /> Back to quote
          </Link>
        </div>
      </div>
    );
  }

  const quote = previewQuery.data;

  return (
    <div className="min-h-screen bg-paper text-graphite print:bg-white">
      {/* Preview banner — sticky, prominent. The contractor needs to
          know they're looking at a preview (not the live customer
          link) so they don't accidentally try to "send" from this
          surface. The banner is hidden on print so a Print → PDF
          export still produces a clean homeowner-shaped document. */}
      <div className="sticky top-0 z-10 border-b-2 border-pencil-yellow bg-pencil-yellow-soft py-2 print:hidden">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-wider text-graphite">
            <Eye className="h-3.5 w-3.5" aria-hidden /> Preview · what the customer sees
          </div>
          <Link
            to={`/quotes/${quote.id}`}
            className="inline-flex items-center gap-1 rounded border border-graphite/20 bg-paper px-3 py-1 font-mono text-xs font-semibold text-graphite hover:bg-paper-warm"
          >
            <ArrowLeft className="h-3 w-3" aria-hidden /> Back to editor
          </Link>
        </div>
      </div>

      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-12 print:px-6 print:py-4">
        <Letterhead quote={quote} />

        <BillingBlock quote={quote} />

        <LineItems quote={quote} />

        <Totals quote={quote} />

        {/* Change orders that have been sent to the homeowner.
            ``readOnly`` suppresses the Approve / Decline buttons —
            this is the contractor's preview, not a real signing
            surface. The pending banner + status pills + amounts
            still render so the contractor sees exactly what the
            homeowner will see. */}
        {quote.change_orders && quote.change_orders.length > 0 && (
          <ChangeOrderList
            quote={quote}
            token=""
            onUpdated={() => {
              /* no-op on preview */
            }}
            readOnly
          />
        )}

        {quote.terms && (
          <section className="mt-8">
            <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
              Terms
            </h3>
            <p className="mt-2 whitespace-pre-wrap text-base leading-relaxed sm:text-sm">
              {quote.terms}
            </p>
          </section>
        )}

        {/* No SignatureBlock + no CommentThread on preview — this
            page is read-only. The contractor is just checking
            rendering, not pretending to be the homeowner. */}

        {quote.valid_until && (
          <p className="mt-10 border-t border-rule pt-4 font-mono text-xs text-graphite-soft">
            Valid through {formatTime(quote.valid_until)}.
          </p>
        )}
      </main>
    </div>
  );
}
